/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CapAlertAndFeatures, FeedAddressLink } from './api';
import { getConfig, getFeedLinks, getSingleAlert } from './api';
import type { CapPresets } from '../../components/types';

interface CombinePendingResult<T> {
  results: T;
  pending: boolean;
}

export const useGetConfig = (
  productConfigKey: string,
): UseQueryResult<CapPresets> => {
  return useQuery({
    queryKey: ['cap-config', productConfigKey],
    queryFn: async () => getConfig(productConfigKey),
  });
};

const useAllFeedLinks = (
  capPresets: CapPresets,
): CombinePendingResult<FeedAddressLink[]> => {
  const queries = useMemo(
    () =>
      capPresets.feeds.map((feed) => ({
        queryKey: ['feedLinks', feed.feedAddress],
        queryFn: async (): Promise<FeedAddressLink[]> =>
          getFeedLinks(feed.feedAddress),
        refetchInterval: capPresets.pollInterval,
      })),
    [capPresets],
  );

  const results = useQueries({
    queries,
    combine: (results) => {
      return {
        results: results
          .flatMap((result) => result.data)
          .filter((result) => !!result) as FeedAddressLink[],
        pending: results.some((result) => result.isPending),
      };
    },
  });
  return results;
};

export const useAllFeedAlerts = (
  capPresets: CapPresets,
): CombinePendingResult<CapAlertAndFeatures[]> => {
  const { results: allFeeds, pending: allFeedPending } =
    useAllFeedLinks(capPresets);

  const queries = useMemo(
    () =>
      allFeeds.map((feed) => ({
        queryKey: ['feedAlert', feed],
        queryFn: async (): Promise<CapAlertAndFeatures> => getSingleAlert(feed),
      })),
    [allFeeds],
  );

  const { alertResults, pending: alertsPending } = useQueries({
    queries,
    combine: (results) => {
      return {
        alertResults: results
          .map((result) => result.data)
          .filter((result) => !!result) as CapAlertAndFeatures[],
        pending: results.some((result) => result.isPending),
      };
    },
  });

  return {
    results: alertResults,
    pending: allFeedPending || alertsPending,
  };
};
